/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid2";

import FormHelperText from "@mui/material/FormHelperText";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

import { updateFamilyAndAssets } from "../../../states/slcObjectives.js";

import {
  valudateTextfield2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { pvAnnuity } from "../../../utils/formula.js";

export default function Death() {
  const familyAndAssets = useSelector((state) => state.objectives.protection.familyAndAssets);

  const [incMonthlyAmt, setIncMonthlyAmt] = useState(familyAndAssets.incMonthlyAmt);
  const [incAnnualAmt, setIncAnnualAmt] = useState(familyAndAssets.incAnnualAmt);
  const [incYearsRequired, setIncYearsRequired] = useState(familyAndAssets.incYearsRequired);
  const [incPctReturnRate, setIncPctReturnRate] = useState(familyAndAssets.incPctReturnRate);
  const [incPctInfRate, setIncPctInfRate] = useState(familyAndAssets.incPctInfRate);
  const [incPctInfAdjRate, setIncPctInfAdjRate] = useState(familyAndAssets.incPctInfAdjRate);
  const [incTotaldSettlementFunds, setIncTotaldSettlementFunds] = useState(familyAndAssets.incTotaldSettlementFunds);

  const [liaMortgage, setLiaMortgage] = useState(familyAndAssets.liaMortgage);
  const [liaDebt, setLiaDebt] = useState(familyAndAssets.liaDebt);
  const [liaDepEducation, setLiaDepEducation] = useState(familyAndAssets.liaDepEducation);
  const [liaFinalExpenses, setLiaFinalExpenses] = useState(familyAndAssets.liaFinalExpenses);
  const [liaOtherDesc, setLiaOtherDesc] = useState(familyAndAssets.liaOtherDesc);
  const [liaOtherDescError, setLiaOtherDescError] = useState();
  const [liaOtherAmt, setLiaOtherAmt] = useState(familyAndAssets.liaOtherAmt);
  const [liaTotaldSettlementFunds, setLiaTotaldSettlementFunds] = useState(familyAndAssets.liaTotaldSettlementFunds);

  const [resLifeInsuranceCover, setResLifeInsuranceCover] = useState(familyAndAssets.resLifeInsuranceCover);
  const [resCashAssets, setResCashAssets] = useState(familyAndAssets.resCashAssets);
  const [resCpf, setResCpf] = useState(familyAndAssets.resCpf);
  const [resInvestments, setResInvestments] = useState(familyAndAssets.resInvestments);
  const [resOthersDesc, setResOthersDesc] = useState(familyAndAssets.resOthersDesc);
  const [resOthersDescError, setResOthersDescError] = useState();
  const [resOthersAmt, setResOthersAmt] = useState(familyAndAssets.resOthersAmt);
  const [resTotalResources, setResTotalResources] = useState(familyAndAssets.resTotalResources);

  const [totalRequiredFunds, setTotalRequiredFunds] = useState(familyAndAssets.totalRequiredFunds);
  const [surplusShortfall, setSurplusShortfall] = useState(familyAndAssets.surplusShortfall);
  const [deviationReason, setDeviationReason] = useState(familyAndAssets.deviationReason);
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcIncome, [incAnnualAmt, incYearsRequired, incPctInfAdjRate]);
  useEffect(calcLiabilities, [liaMortgage, liaDebt, liaDepEducation, liaFinalExpenses, liaOtherAmt]);
  useEffect(calcTotalFundsRequired, [incTotaldSettlementFunds, liaTotaldSettlementFunds]);
  useEffect(calcTotalResources, [resLifeInsuranceCover, resCashAssets, resCpf, resInvestments, resOthersAmt]);
  useEffect(calcSurplusOrShortfall, [totalRequiredFunds, resTotalResources]);

  /**==============================
   * FUNCTIONS
   ==============================*/
  function calcIncome() {
    //CALCULATE PV
    let presentValue = pvAnnuity(
      Number(removeCommaFromCurrency(incAnnualAmt)),
      Number(removeCommaFromCurrency(incPctInfAdjRate)) / 100,
      Number(removeCommaFromCurrency(incYearsRequired)),
      1
    );

    presentValue = convertLocaleString(presentValue, "en-US", 2);
    updateRedux("incTotaldSettlementFunds", presentValue, updateFamilyAndAssets);
    setIncTotaldSettlementFunds(presentValue);
  }

  function calcLiabilities() {
    let totalLiabilitities =
      Number(removeCommaFromCurrency(liaMortgage)) +
      Number(removeCommaFromCurrency(liaDebt)) +
      Number(removeCommaFromCurrency(liaDepEducation)) +
      Number(removeCommaFromCurrency(liaFinalExpenses)) +
      Number(removeCommaFromCurrency(liaOtherAmt));

    totalLiabilitities = convertLocaleString(totalLiabilitities, "en-US", 2);

    updateRedux("liaTotaldSettlementFunds", totalLiabilitities, updateFamilyAndAssets);
    setLiaTotaldSettlementFunds(totalLiabilitities);
  }

  function calcTotalResources() {
    let totalResources =
      Number(removeCommaFromCurrency(resLifeInsuranceCover)) +
      Number(removeCommaFromCurrency(resCashAssets)) +
      Number(removeCommaFromCurrency(resCpf)) +
      Number(removeCommaFromCurrency(resInvestments)) +
      Number(removeCommaFromCurrency(resOthersAmt));

    totalResources = convertLocaleString(totalResources, "en-US", 2);

    updateRedux("resTotalResources", totalResources, updateFamilyAndAssets);
    setResTotalResources(totalResources);
  }

  function calcTotalFundsRequired() {
    let totalRequiredFunds =
      Number(removeCommaFromCurrency(incTotaldSettlementFunds)) +
      Number(removeCommaFromCurrency(liaTotaldSettlementFunds));

    totalRequiredFunds = convertLocaleString(totalRequiredFunds, "en-US", 2);

    updateRedux("totalRequiredFunds", totalRequiredFunds, updateFamilyAndAssets);
    setTotalRequiredFunds(totalRequiredFunds);
  }

  function calcSurplusOrShortfall() {
    let surplusShortfall =
      Number(removeCommaFromCurrency(resTotalResources)) - Number(removeCommaFromCurrency(totalRequiredFunds));

    surplusShortfall = convertLocaleString(surplusShortfall, "en-US", 2);

    updateRedux("surplusShortfall", surplusShortfall, updateFamilyAndAssets);
    setSurplusShortfall(surplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    //INCOME
    if (id === "incMonthlyAmt") {
      input_data.fieldName = "Monthly income";
      input_data.fieldVariable = incMonthlyAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        let annualAmount = Number(removeCommaFromCurrency(result.value)) * 12;
        annualAmount = convertLocaleString(annualAmount, "en-US", 2);

        updateRedux("incMonthlyAmt", result.value, updateFamilyAndAssets);
        updateRedux("incAnnualAmt", annualAmount, updateFamilyAndAssets);
        setIncMonthlyAmt(result.value);
        setIncAnnualAmt(annualAmount);
      }
    }

    if (id === "incAnnualAmt") {
      input_data.fieldName = "Annual income";
      input_data.fieldVariable = incAnnualAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setIncAnnualAmt(result.value);
        updateRedux("incAnnualAmt", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "incYearsRequired") {
      input_data.fieldName = "Years";
      input_data.fieldVariable = incYearsRequired;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setIncYearsRequired(result.value);
        updateRedux("incYearsRequired", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "incPctReturnRate") {
      input_data.fieldName = "Rate of return (%)";
      input_data.fieldVariable = incPctReturnRate;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setIncPctReturnRate(result.value);
        updateRedux("incPctReturnRate", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "incPctInfRate") {
      input_data.fieldName = "Assumed inflation rate (%)";
      input_data.fieldVariable = incPctInfRate;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setIncPctInfRate(result.value);
        updateRedux("incPctInfRate", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "incPctInfAdjRate") {
      input_data.fieldName = "Inflation adjusted Rate";
      input_data.fieldVariable = incPctInfAdjRate;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setIncPctInfAdjRate(result.value);
        updateRedux("incPctInfAdjRate", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "incTotaldSettlementFunds") {
      input_data.fieldName = "Total required income";
      input_data.fieldVariable = incTotaldSettlementFunds;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setIncTotaldSettlementFunds(result.value);
        updateRedux("incTotaldSettlementFunds", result.value, updateFamilyAndAssets);
      }
    }

    //LIABILITY
    if (id === "liaMortgage") {
      input_data.fieldName = "Mortgage";
      input_data.fieldVariable = liaMortgage;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLiaMortgage(result.value);
        updateRedux("liaMortgage", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "liaDebt") {
      input_data.fieldName = "Debt";
      input_data.fieldVariable = liaDebt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLiaDebt(result.value);
        updateRedux("liaDebt", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "liaDepEducation") {
      input_data.fieldName = "Dependant education";
      input_data.fieldVariable = liaDepEducation;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLiaDepEducation(result.value);
        updateRedux("liaDepEducation", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "liaFinalExpenses") {
      input_data.fieldName = "Final expense";
      input_data.fieldVariable = liaFinalExpenses;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLiaFinalExpenses(result.value);
        updateRedux("liaFinalExpenses", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "liaOtherDesc") {
      input_data.fieldName = "Others description";
      input_data.fieldVariable = liaOtherDesc;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setLiaOtherDesc(result.value);
        updateRedux("liaOtherDesc", result.value, updateFamilyAndAssets);
      }

      setLiaOtherDescError(result.errorMessage);
    }

    if (id === "liaOtherAmt") {
      input_data.fieldName = "Other amount";
      input_data.fieldVariable = liaOtherAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLiaOtherAmt(result.value);
        updateRedux("liaOtherAmt", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "liaTotaldSettlementFunds") {
      input_data.fieldName = "Total liability settlement fund";
      input_data.fieldVariable = liaTotaldSettlementFunds;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLiaTotaldSettlementFunds(result.value);
        updateRedux("liaTotaldSettlementFunds", result.value, updateFamilyAndAssets);
      }
    }
    /////////////////////

    if (id === "totalRequiredFunds") {
      input_data.fieldName = "Total liability settlement fund";
      input_data.fieldVariable = totalRequiredFunds;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setTotalRequiredFunds(result.value);
        updateRedux("totalRequiredFunds", result.value, updateFamilyAndAssets);
      }
    }

    //EXISTING RESOURCES
    if (id === "resLifeInsuranceCover") {
      input_data.fieldName = "Insurance cover";
      input_data.fieldVariable = resLifeInsuranceCover;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResLifeInsuranceCover(result.value);
        updateRedux("resLifeInsuranceCover", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "resCashAssets") {
      input_data.fieldName = "Cash assets";
      input_data.fieldVariable = resCashAssets;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResCashAssets(result.value);
        updateRedux("resCashAssets", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "resCpf") {
      input_data.fieldName = "CPF";
      input_data.fieldVariable = resCpf;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResCpf(result.value);
        updateRedux("resCpf", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "resInvestments") {
      input_data.fieldName = "Investments";
      input_data.fieldVariable = resInvestments;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResInvestments(result.value);
        updateRedux("resInvestments", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "resOthersDesc") {
      input_data.fieldName = "Other resources description";
      input_data.fieldVariable = resOthersDesc;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setResOthersDesc(result.value);
        updateRedux("resOthersDesc", result.value, updateFamilyAndAssets);
      }

      setResOthersDescError(result.errorMessage);
    }

    if (id === "resOthersAmt") {
      input_data.fieldName = "Other resource amount";
      input_data.fieldVariable = resOthersAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResOthersAmt(result.value);
        updateRedux("resOthersAmt", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "resTotalResources") {
      input_data.fieldName = "Total resource amount";
      input_data.fieldVariable = resTotalResources;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResTotalResources(result.value);
        updateRedux("resTotalResources", result.value, updateFamilyAndAssets);
      }
    }

    ////////////
    if (id === "surplusShortfall") {
      input_data.fieldName = "Surplus / Shortfall";
      input_data.fieldVariable = surplusShortfall;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setSurplusShortfall(result.value);
        updateRedux("surplusShortfall", result.value, updateFamilyAndAssets);
      }
    }

    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updateFamilyAndAssets);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={1} size={12}>
        <GridSectionHeader
          sectionName="In the Event of Death"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            backgroundColor: "#10B981",
          }}
        />

        {/*==============================
        INCOME
        ==============================*/}
        <Grid size={9}>
          <Typography variant="h5" sx={{ fontWeight: "bold", textDecoration: "underline" }}>
            Income
          </Typography>
        </Grid>
      </Grid>

      {/*==============================
      MONTHLY AMOUNT NEEDS
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Monthly Amount Required
        </Typography>
      </Grid>
      <GridTextfield
        id="incMonthlyAmt"
        key="incMonthlyAmt"
        columnSize="3"
        label="Monthly"
        isCurrency={true}
        currState={incMonthlyAmt}
        onChange={handleChange}
      />

      {/*==============================
        ANNUAL AMOUNT NEEDS
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Annual Amount Required (pmt)
        </Typography>
      </Grid>
      <GridTextfield
        id="incAnnualAmt"
        key="incAnnualAmt"
        columnSize="3"
        label="Annually"
        isCurrency={true}
        currState={incAnnualAmt}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        NUMBER OF YEARS REQUIRED
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Number of Years Required (n)
        </Typography>
      </Grid>

      <GridTextfield
        id="incYearsRequired"
        key="incYearsRequired"
        columnSize="3"
        currState={incYearsRequired}
        onChange={handleChange}
      />
      {/*==============================
        INVESTMENT RATE OF RETURN
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 0.5,
          }}
        >
          Investment Rate of Return (%) (R)
        </Typography>
        <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
      </Grid>

      <GridTextfield
        id="incPctReturnRate"
        key="incPctReturnRate"
        columnSize="3"
        label="Percent"
        currState={incPctReturnRate}
        onChange={handleChange}
      />
      {/*==============================
        ASSUMED INFLATION RATE
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 0.5,
          }}
        >
          Assumed Inflation Rate. (%) (i)
        </Typography>
        <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
      </Grid>
      <GridTextfield
        id="incPctInfRate"
        key="incPctInfRate"
        columnSize="3"
        label="Percent"
        currState={incPctInfRate}
        onChange={handleChange}
      />

      {/*==============================
        INFLATION ADJUSTED RATE OF RETURN FROM INVESTMENT
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Inflation-adjusted Rate of Return from investments(%) (i)
        </Typography>
      </Grid>
      <GridTextfield
        id="incPctInfAdjRate"
        key="incPctInfAdjRate"
        columnSize="3"
        label="Percent"
        currState={incPctInfAdjRate}
        onChange={handleChange}
      />

      {/*==============================
        FUND REQUIRED TO PROVIDE INCOME
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          <b>(A) Funds Required to Provide Income (pv)</b>
        </Typography>
      </Grid>

      <GridTextfield
        id="incTotaldSettlementFunds"
        key="incTotaldSettlementFunds"
        columnSize="3"
        isCurrency={true}
        currState={incTotaldSettlementFunds}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        LIABILITIES
      ==============================*/}
      <Grid size={12}>
        <Typography variant="h5" sx={{ pt: 2.5, textDecoration: "underline" }}>
          <b>Liabilities</b>
        </Typography>
      </Grid>

      {/*==============================
        MORTGAGE
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Outstanding Mortgage
        </Typography>
      </Grid>

      <GridTextfield
        id="liaMortgage"
        key="liaMortgage"
        columnSize="3"
        label=""
        isCurrency={true}
        currState={liaMortgage}
        onChange={handleChange}
      />

      {/*==============================
        DEBT
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Debt (Loans, Credit cards, Overdraft, etc.)
        </Typography>
      </Grid>

      <GridTextfield
        id="liaDebt"
        key="liaDebt"
        columnSize="3"
        label=""
        isCurrency={true}
        currState={liaDebt}
        onChange={handleChange}
      />

      {/*==============================
        FUNDS REQUIRED FOR DEPENDANT'S EDUCATION
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Funds Required for Dependants’ Education (tertiary, etc.)
        </Typography>
      </Grid>

      <GridTextfield
        id="liaDepEducation"
        key="liaDepEducation"
        columnSize="3"
        isCurrency={true}
        currState={liaDepEducation}
        onChange={handleChange}
      />

      {/*==============================
        FINAL EXPENSES
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Final Expenses (Medical, Taxes, Funeral, etc.)
        </Typography>
      </Grid>

      <GridTextfield
        id="liaFinalExpenses"
        key="liaFinalExpenses"
        columnSize="3"
        isCurrency={true}
        currState={liaFinalExpenses}
        onChange={handleChange}
      />

      {/*==============================
        LIABILITIES - OTHERS
        ==============================*/}
      <Grid size={1}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Others:
        </Typography>
      </Grid>

      <GridTextfield
        id="liaOtherDesc"
        key="liaOtherDesc"
        columnSize="8"
        currState={liaOtherDesc}
        onChange={handleChange}
        error={liaOtherDescError}
        helperText={liaOtherDescError}
      />

      <GridTextfield
        id="liaOtherAmt"
        key="liaOtherAmt"
        columnSize="3"
        isCurrency={true}
        currState={liaOtherAmt}
        onChange={handleChange}
      />

      {/*==============================
        B. FUND REQUIRED TO SETTLE LIABILITIES
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (B) Funds Required to Settle Liabilities
        </Typography>
      </Grid>

      <GridTextfield
        id="liaTotaldSettlementFunds"
        key="liaTotaldSettlementFunds"
        columnSize="3"
        isCurrency={true}
        currState={liaTotaldSettlementFunds}
        onChange={handleChange}
        disabled={true}
      />

      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (C) Total Funds Required = (A + B)
        </Typography>
      </Grid>

      <GridTextfield
        id="totalRequiredFunds"
        key="totalRequiredFunds"
        columnSize="3"
        isCurrency={true}
        currState={totalRequiredFunds}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        EXISTING RESOURCES ALLOCATED (CURRENT VALUES)
      ==============================*/}
      <Grid size={12}>
        <Typography
          variant="h5"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
            textDecoration: "underline",
          }}
        >
          Existing Resources Allocated (Current Value)
        </Typography>
      </Grid>

      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Life Insurance Coverage
        </Typography>
      </Grid>

      <GridTextfield
        id="resLifeInsuranceCover"
        key="resLifeInsuranceCover"
        columnSize="3"
        isCurrency={true}
        currState={resLifeInsuranceCover}
        onChange={handleChange}
      />

      {/*==============================
        EXISTING RESOURCE ALLOCATED - CASH ASSETS
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Cash Assets (Savings, Fixed Deposits, etc.)
        </Typography>
      </Grid>

      <GridTextfield
        id="resCashAssets"
        key="resCashAssets"
        columnSize="3"
        isCurrency={true}
        currState={resCashAssets}
        onChange={handleChange}
      />

      {/*==============================
        EXISTING RESOURCE ALLOCATED - CPF
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          CPF
        </Typography>
      </Grid>

      <GridTextfield
        id="resCpf"
        key="resCpf"
        columnSize="3"
        isCurrency={true}
        currState={resCpf}
        onChange={handleChange}
      />

      {/*==============================
        EXISTING RESOURCE ALLOCATED - INVESTMENT
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
          }}
        >
          Investments (Bonds, Unit Trusts, Shares, etc.)
        </Typography>
      </Grid>

      <GridTextfield
        id="resInvestments"
        key="resInvestments"
        columnSize="3"
        isCurrency={true}
        currState={resInvestments}
        onChange={handleChange}
      />

      {/*==============================
        EXISTING RESOURCE ALLOCATED - OTHERS
        ==============================*/}
      <Grid size={1}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Others:
        </Typography>
      </Grid>

      <GridTextfield
        id="resOthersDesc"
        key="resOthersDesc"
        columnSize="8"
        currState={resOthersDesc}
        onChange={handleChange}
        error={resOthersDescError}
        helperText={resOthersDescError}
      />

      <GridTextfield
        id="resOthersAmt"
        key="resOthersAmt"
        columnSize="3"
        isCurrency={true}
        currState={resOthersAmt}
        onChange={handleChange}
      />

      {/*==============================
        TOTAL
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (D) Total Resource Allocated
        </Typography>
      </Grid>

      <GridTextfield
        id="resTotalResources"
        key="resTotalResources"
        columnSize="3"
        isCurrency={true}
        currState={resTotalResources}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        SURPLUS / SHORTFALL
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.5,
            fontWeight: "bold",
          }}
        >
          (D - C) Surplus / Shortfall
        </Typography>
      </Grid>

      <GridTextfield
        id="surplusShortfall"
        key="surplusShortfall"
        columnSize="3"
        label=""
        isCurrency={true}
        currState={surplusShortfall}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        REASON FOR DEVIATION
      ==============================*/}
      <Grid size={12}>
        <Typography variant="body1">
          <b>Reason(s) for Deviations:</b>
        </Typography>

        <TextareaAutosize
          id="deviationReason"
          placeholder="Type here..."
          style={{ width: "100%" }}
          minRows={5}
          onChange={handleChange}
          defaultValue={deviationReason}
        />
        {deviationReasonError ? (
          <FormHelperText error={deviationReasonError}>{deviationReasonError}</FormHelperText>
        ) : (
          ""
        )}

        <Typography variant="body2">
          # ‘Number of Years Required (n)’ should base on life expectancy of client less client’s year of birth. If
          greater than this recommended guide, Representative to justify. Life expectancy for male is 82 and female is
          86.
        </Typography>
      </Grid>
    </>
  );
}
